import * as postRecipeCalculate from 'api/recipe/PostRecipeCalculateV1';
import { Optional } from 'functions/promiseExtensions';
import { SimpleNumber } from 'types/_general/SimpleNumber';

export type Type = {
	calories: number;
	weight: number;
	price: number | null;
	carbonDioxideValue: number | null;
	carbonDioxideLabel: CarbonDioxideLabel;
	ingredientNames: IngredientNames[];
	accessoryNames: AccessoryNames[];
	nutrientValues: NutrientValues[];
	nutrientValuesPerIngredient: NutrientValuesPerIngredient[];
	pricePerIngredient: PricePerIngredient[];
	pricePerAccessory: PricePerAccessory[];
	carbonDioxideValuePerIngredient: CarbonDioxideValuePerIngredient[];
	carbonDioxideValuePerAccessory: CarbonDioxideValuePerAccessory[];
	allergens: Allergens[];
	allergensPerIngredient: AllergensPerIngredient[];
	seasons: Seasons[];
	seasonsPerIngredient: SeasonsPerIngredient[];
};

export function create() {
	return {
		calories: 0,
		weight: 0,
		price: null,
		carbonDioxideValue: null,
		carbonDioxideLabel: createCarbonDioxideLabel(),
		ingredientNames: [],
		accessoryNames: [],
		nutrientValues: [],
		nutrientValuesPerIngredient: [],
		pricePerIngredient: [],
		pricePerAccessory: [],
		carbonDioxideValuePerIngredient: [],
		carbonDioxideValuePerAccessory: [],
		allergens: [],
		allergensPerIngredient: [],
		seasons: [],
		seasonsPerIngredient: [],
		seasonsHistogram: {},
	};
}

export function mapNutrientsToKeyValueObject(
	obj: Type,
	ingredientId: string
): { [nutrientId: string]: number | null } {
	for (const ingredient of obj.nutrientValuesPerIngredient) {
		if (ingredient.ingredientId !== ingredientId) continue;
		const output: { [nutrientId: string]: number | null } = {};
		for (const i of ingredient.nutrientValues) {
			const id = i.nutrientId;
			output[id] = SimpleNumber.round(i.total, 2);
		}
		return output;
	}
	return {};
}

export type CarbonDioxideLabel = {
	label: string;
	color: string;
};

export function createCarbonDioxideLabel() {
	return {
		label: '',
		color: '',
	};
}

export async function callApi(input: {
	ingredients: postRecipeCalculate.Ingredients[];
	accessories: postRecipeCalculate.Accessories[];
	currencyCode: string | null;
}): Promise<Type> {
	const response = await postRecipeCalculate.callApi(input);
	return response.getOrDefault(create());
}

export type IngredientNames = {
	ingredientIndex: number;
	ingredientNames: Translation;
};

export type Translation = {
	[key: string]: string;
};

export type AccessoryNames = {
	accessoryIndex: number;
	accessoryNames: Translation;
};

export type NutrientValues = {
	nutrientId: string;
	nutrient: string;
	category: string;
	total: number;
	unit: string;
	isMacroNutrient: boolean;
	nutrientCategorySortOrder: number;
};

export type NutrientValuesPerIngredient = {
	ingredientIndex: number;
	ingredientId: string;
	nutrientValues: NutrientValues[];
};

export type PricePerIngredient = {
	ingredientIndex: number;
	ingredientId: string;
	price: number;
};

export type PricePerAccessory = {
	accessoryIndex: number;
	accessoryId: string;
	price: number;
};

export type CarbonDioxideValuePerIngredient = {
	ingredientIndex: number;
	ingredientId: string;
	value: number;
};

export type CarbonDioxideValuePerAccessory = {
	accessoryIndex: number;
	accessoryId: string;
	value: number;
};

export type Allergens = {
	allergen: string;
	logic: 'Positive' | 'Negative' | 'MayContain';
	iconSvgUrl: string | null;
};

export type AllergensPerIngredient = {
	ingredientIndex: number;
	ingredientId: string;
	allergens: Allergens[];
};

export type Seasons = {
	season: string;
	associatedMonthsFlag: number | null;
	iconSvgUrl: string | null;
};

export type SeasonsPerIngredient = {
	ingredientIndex: number;
	ingredientId: string;
	seasons: Seasons[];
};

export function calculateTotalById(calculation: Type, ids: string[], parts: number): number {
	return Optional.Maybe(getByIds(calculation.nutrientValues, ids))
		.map((e) => (parts > 0 ? e.total / parts : 0))
		.orElse(() => 0);
}

export function getUnitById(calculation: Type, ids: string[]): string {
	return Optional.Maybe<NutrientValues>(getByIds(calculation.nutrientValues, ids))
		.map((e) => e.unit)
		.orElse(() => '');
}

export function calculateDailyValueById(
	calculation: Type,
	ids: string[],
	parts: number,
	dailyValues: number[]
): number {
	const nutrientValues: NutrientValues[] = calculation.nutrientValues;
	const nutrientValue: NutrientValues | undefined = getByIds(calculation.nutrientValues, ids);
	const dailyValue = dailyValues
		.zip(ids)
		.filter(([_dailyValue, id]) => nutrientValues.some((y) => y.nutrientId === id))
		.map(([dailyValue, _id]) => dailyValue)
		.firstOrDefault();

	if (!nutrientValue || !dailyValue) return 0;

	return (nutrientValue.total / parts / dailyValue) * 100;
}

export function getByIds(obj: NutrientValues[], ids: string[]): NutrientValues | undefined {
	return obj.find((e) => ids.includes(e.nutrientId));
}
